import dayjs from 'dayjs';
import 'dayjs/locale/es';  // Spanish locale for dayjs (~2KB)
import 'dayjs/locale/fr';  // French locale for dayjs (~2KB)
import 'dayjs/locale/de';  // German locale for dayjs (~2KB)
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// English locale (split by namespace for maintainability)
import enCommon from './locales/en/common.json';
import enComponents from './locales/en/components.json';
import enDialogs from './locales/en/dialogs.json';
import enEstimator from './locales/en/estimator.json';
import enLogin from './locales/en/login.json';
import enScreens from './locales/en/screens';
import enSettings from './locales/en/settings.json';
import enSuperAdmin from './locales/en/superAdmin.json';
import enWidgets from './locales/en/widgets.json';

// Spanish locale
import esCommon from './locales/es/common.json';
import esComponents from './locales/es/components.json';
import esDialogs from './locales/es/dialogs.json';
import esEstimator from './locales/es/estimator.json';
import esLogin from './locales/es/login.json';
import esScreens from './locales/es/screens';
import esSettings from './locales/es/settings.json';
import esSuperAdmin from './locales/es/superAdmin.json';
import esWidgets from './locales/es/widgets.json';

// French locale
import frCommon from './locales/fr/common.json';
import frComponents from './locales/fr/components.json';
import frDialogs from './locales/fr/dialogs.json';
import frEstimator from './locales/fr/estimator.json';
import frLogin from './locales/fr/login.json';
import frScreens from './locales/fr/screens';
import frSettings from './locales/fr/settings.json';
import frSuperAdmin from './locales/fr/superAdmin.json';
import frWidgets from './locales/fr/widgets.json';

// German locale
import deCommon from './locales/de/common.json';
import deComponents from './locales/de/components.json';
import deDialogs from './locales/de/dialogs.json';
import deEstimator from './locales/de/estimator.json';
import deLogin from './locales/de/login.json';
import deScreens from './locales/de/screens';
import deSettings from './locales/de/settings.json';
import deSuperAdmin from './locales/de/superAdmin.json';
import deWidgets from './locales/de/widgets.json';

// Combine namespace files into full translation objects
const en = {
  common: enCommon,
  components: enComponents,
  dialogs: enDialogs,
  estimator: enEstimator,
  login: enLogin,
  screens: enScreens,
  settings: enSettings,
  superAdmin: enSuperAdmin,
  widgets: enWidgets,
};

const es = {
  common: esCommon,
  components: esComponents,
  dialogs: esDialogs,
  estimator: esEstimator,
  login: esLogin,
  screens: esScreens,
  settings: esSettings,
  superAdmin: esSuperAdmin,
  widgets: esWidgets,
};

const fr = {
  common: frCommon,
  components: frComponents,
  dialogs: frDialogs,
  estimator: frEstimator,
  login: frLogin,
  screens: frScreens,
  settings: frSettings,
  superAdmin: frSuperAdmin,
  widgets: frWidgets,
};

const de = {
  common: deCommon,
  components: deComponents,
  dialogs: deDialogs,
  estimator: deEstimator,
  login: deLogin,
  screens: deScreens,
  settings: deSettings,
  superAdmin: deSuperAdmin,
  widgets: deWidgets,
};

// Supported languages
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
] as const;

export type SupportedLanguage = typeof supportedLanguages[number]['code'];

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: en,
      },
      es: {
        translation: es,
      },
      fr: {
        translation: fr,
      },
      de: {
        translation: de,
      },
    },
    lng: 'en',
    fallbackLng: 'en',
    initImmediate: false, // Synchronous init for testing
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false, // Disable Suspense for testing
    },
  });

// Set initial dayjs locale to match i18n default
dayjs.locale('en');

// Development-only: Track missing translation keys
if (process.env.NODE_ENV === 'development') {
  i18n.on('missingKey', (lngs, namespace, key, res) => {
    console.error(`[i18n] Missing key: "${key}" for languages: ${lngs.join(', ')}`);

    // Track missing keys in session storage for debugging
    try {
      const missingKeys = JSON.parse(sessionStorage.getItem('i18n_missing_keys') || '[]');
      missingKeys.push({ key, languages: lngs, timestamp: Date.now() });
      sessionStorage.setItem('i18n_missing_keys', JSON.stringify(missingKeys));
    } catch (error) {
      // Silently fail if sessionStorage is unavailable
    }
  });
}

/**
 * Change the current language
 * @param language - Language code ('en' or 'es')
 */
export const changeLanguage = async (language: SupportedLanguage): Promise<void> => {
  await i18n.changeLanguage(language);
  dayjs.locale(language);  // Sync dayjs locale for react-calendar-timeline
};

/**
 * Get the current language code
 */
export const getCurrentLanguage = (): SupportedLanguage => {
  return (i18n.language || 'en') as SupportedLanguage;
};

export default i18n;
